/**
 * Pure combat math: idle spin drain, clash resolution, and the burst meter.
 * No Phaser, no randomness — deterministic and unit-testable.
 */
import { SIM } from './config.ts';
import type { BeyStats, BeyType, SpinDir } from './stats.ts';
import { typeAdvantage } from './stats.ts';

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * Idle spin lost per second for a bey, derived from its stamina stat (1–10).
 * Higher stamina => slower drain.
 */
export function idleDrainPerSecond(stamina: number): number {
  const t = clamp((stamina - 1) / 9, 0, 1);
  const seconds = lerp(
    SIM.IDLE_EMPTY_SECONDS_AT_MIN_STAMINA,
    SIM.IDLE_EMPTY_SECONDS_AT_MAX_STAMINA,
    t,
  );
  return SIM.MAX_SPIN / seconds;
}

export interface ClashSide {
  stats: BeyStats;
  type: BeyType;
  spin: number;
  /** Current burst meter value (0..METER_MAX). */
  burst: number;
  /** Spin direction; opposite-spin clashes steal more spin. Defaults right. */
  spinDir?: SpinDir;
}

export interface ClashOutcome {
  /** Which side won the exchange. */
  aggressor: 'a' | 'b';
  /** Spin to subtract from each side (>= 0). */
  spinLossA: number;
  spinLossB: number;
  /** Knockback impulse magnitude to apply to each side (>= 0). */
  knockbackA: number;
  knockbackB: number;
  /** Burst meter to add to each side (>= 0). */
  burstGainA: number;
  burstGainB: number;
  /** Whether this clash counts as a "big hit" (>= burst threshold). */
  bigHit: boolean;
}

/** "Clash power" decides who wins the exchange. */
function clashPower(side: ClashSide): number {
  // Attack and weight drive aggression; spin scales it (a dying top hits weakly).
  const spinFactor = clamp(side.spin / SIM.MAX_SPIN, 0.15, 1);
  return side.stats.attack * (1 + side.stats.weight * 0.1) * spinFactor;
}

/**
 * Resolve a single collision between two beys.
 *
 * `impactSpeed` is the relative approach speed at contact (engine units). The
 * harder the beys close on each other, the bigger the exchange.
 */
export function resolveClash(a: ClashSide, b: ClashSide, impactSpeed: number): ClashOutcome {
  const speed = Math.max(0, impactSpeed);
  const powerA = clashPower(a);
  const powerB = clashPower(b);
  const aWins = powerA >= powerB;

  let spinLossA = sideSpinLoss(b, a, speed, /*isAggressor=*/ aWins);
  let spinLossB = sideSpinLoss(a, b, speed, /*isAggressor=*/ !aWins);

  // Opposite-spin clash: the winner absorbs the loser's rotation — the loser
  // bleeds far more spin while the winner is shielded (DESIGN §3.4).
  const opposite = (a.spinDir ?? 'right') !== (b.spinDir ?? 'right');
  if (opposite) {
    const c = SIM.CLASH;
    if (aWins) {
      spinLossB *= c.OPPOSITE_SPIN_LOSS_MULT;
      spinLossA *= c.OPPOSITE_SPIN_WINNER_RELIEF;
    } else {
      spinLossA *= c.OPPOSITE_SPIN_LOSS_MULT;
      spinLossB *= c.OPPOSITE_SPIN_WINNER_RELIEF;
    }
  }

  const knockbackA = sideKnockback(b, a, speed, /*isAggressor=*/ aWins);
  const knockbackB = sideKnockback(a, b, speed, /*isAggressor=*/ !aWins);

  const bigHit = speed >= SIM.BURST.IMPACT_THRESHOLD;
  const burstGainA = burstGain(a, speed);
  const burstGainB = burstGain(b, speed);

  return {
    aggressor: aWins ? 'a' : 'b',
    spinLossA,
    spinLossB,
    knockbackA,
    knockbackB,
    burstGainA,
    burstGainB,
    bigHit,
  };
}

/** Spin lost by `victim` when struck by `attacker`. */
function sideSpinLoss(
  attacker: ClashSide,
  victim: ClashSide,
  speed: number,
  victimIsAggressor: boolean,
): number {
  const c = SIM.CLASH;
  const typeMult = typeAdvantage(
    attacker.type,
    victim.type,
    c.TYPE_ADVANTAGE,
    c.TYPE_DISADVANTAGE,
  );
  const attackTerm = 1 + attacker.stats.attack * c.ATTACK_K;
  const defenseTerm = 1 + victim.stats.defense * c.DEFENSE_K;

  let loss = (c.BASE_SPIN_LOSS * speed * attackTerm * typeMult) / defenseTerm;
  if (victimIsAggressor) loss *= 1 - c.AGGRESSOR_RELIEF;
  return Math.max(c.MIN_SPIN_LOSS, loss);
}

/** Knockback magnitude applied to `victim` when struck by `attacker`. */
function sideKnockback(
  attacker: ClashSide,
  victim: ClashSide,
  speed: number,
  victimIsAggressor: boolean,
): number {
  const k = SIM.KNOCKBACK;
  const attackTerm = 1 + attacker.stats.attack * 0.12;
  const weightResist = 1 + victim.stats.weight * k.WEIGHT_K;
  let kb = (k.BASE * speed * attackTerm) / weightResist;
  if (!victimIsAggressor) kb *= k.AGGRESSOR_BONUS; // the loser gets shoved harder
  return Math.max(0, kb);
}

/** Burst meter gained by `side` from a hit of the given impact speed. */
function burstGain(side: ClashSide, speed: number): number {
  const b = SIM.BURST;
  if (speed < b.IMPACT_THRESHOLD) return 0;
  const over = speed - b.IMPACT_THRESHOLD;
  const durabilityResist = 1 + side.stats.durability * b.DURABILITY_K;
  return (over * b.GAIN_K) / durabilityResist;
}

/** Apply burst-meter decay over `dt` seconds; returns the new meter value. */
export function decayBurst(meter: number, dt: number): number {
  return clamp(meter - SIM.BURST.DECAY_PER_SEC * dt, 0, SIM.BURST.METER_MAX);
}

export const isBurst = (meter: number): boolean => meter >= SIM.BURST.METER_MAX;
